/**
 * Revenue Compliance — Tools sub-tab.
 *
 * Read-only investigation surface over the persisted revenue-assurance findings:
 * KPI cards (revenue at risk, overdue-and-connected, credits, discounts), a
 * by-class breakdown, and a filterable findings table. "Recompute now" runs the
 * sweep server-side; "CSV" downloads the latest findings. Gated server-side and
 * by the Tools nav to root / billing_supervisor / installation_admin.
 */
import { escapeHtml, formatCurrency, formatNumber, formatDateTimeFull, formatDate } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const adminAPI = api;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

const FLAG_LABELS = {
  overdue_connected: 'Overdue & connected',
  non_payment: 'Non-payment',
  no_payment_in_period: 'No payment in period',
  unreviewed_credit: 'Unreviewed credit',
  has_discount: 'Has discount',
  connected_no_active_sub: 'No active sub',
  high_credit_limit: 'High credit limit',
};

const KPI_CARDS = [
  { get: (s) => (s.flag_counts || {}).overdue_connected || 0, label: 'Overdue & connected' },
  { get: (s) => (s.money || {}).revenue_at_risk || 0, label: 'Revenue at risk / mo', money: true },
  { get: (s) => (s.money || {}).credits_granted || 0, label: 'Manual credits', money: true },
  { get: (s) => (s.flag_counts || {}).has_discount || 0, label: 'Discount accounts' },
  { get: (s) => (s.money || {}).discount_monthly || 0, label: 'Discount value / mo', money: true },
  { get: (s) => (s.flag_counts || {}).no_payment_in_period || 0, label: 'No payment in period' },
  { get: (s) => s.flagged || 0, label: 'Total flagged' },
];

const state = { loading: false, loaded: false, summary: {}, items: [], total: 0, run: null, flag: '', error: null };
const creditState = { loading: false, loaded: false, items: [], pendingCount: 0, pendingAmount: 0,
  status: 'pending', description: '', descriptions: [], error: null };

function _el(id) { return document.getElementById(id); }
function _money(v) { return formatCurrency(v); }

function renderKpis() {
  const wrap = _el('revenueComplianceKpis');
  if (!wrap) return;
  const s = state.summary || {};
  wrap.innerHTML = KPI_CARDS.map((c) => {
    const v = c.get(s);
    const display = c.money ? _money(v) : formatNumber(v);
    return `<div class="kpi-card" style="min-width:130px;"><h4 style="margin:0; font-size:11px; color:var(--sub);">${escapeHtml(c.label)}</h4><div class="value" style="font-size:18px; font-weight:700;">${display}</div></div>`;
  }).join('');
}

function renderByClass() {
  const wrap = _el('revenueComplianceByClass');
  if (!wrap) return;
  const byClass = (state.summary || {}).by_class || {};
  // Only classes that actually have active non-payers; sorted by money at risk.
  const rows = Object.entries(byClass)
    .filter(([, v]) => (v.accounts || 0) > 0)
    .sort((a, b) => (b[1].revenue_at_risk || 0) - (a[1].revenue_at_risk || 0));
  if (!rows.length) { wrap.innerHTML = ''; return; }
  // Split across two columns to cut the vertical footprint.
  const mid = Math.ceil(rows.length / 2);
  const tableFor = (rs) => rs.length
    ? `<table style="width:100%; font-size:12px;"><thead><tr><th style="text-align:left;">Class</th><th style="text-align:center;">Accounts</th><th style="text-align:right;">At risk / mo</th></tr></thead><tbody>
      ${rs.map(([cls, v]) => `<tr><td>${escapeHtml(cls)}</td><td style="text-align:center;">${v.accounts || 0}</td><td style="text-align:right;">${_money(v.revenue_at_risk)}</td></tr>`).join('')}
      </tbody></table>`
    : '';
  wrap.innerHTML = `
    <div style="font-size:12px; color:var(--sub); margin-bottom:4px;">By customer class (active non-payers)</div>
    <div style="display:grid; grid-template-columns:1fr 1fr; gap:16px; align-items:start;">
      <div>${tableFor(rows.slice(0, mid))}</div>
      <div>${tableFor(rows.slice(mid))}</div>
    </div>`;
}

// The findings table collapses for readability, so its summary carries the count —
// otherwise a collapsed section gives no hint of what is inside.
function renderFindingsCount() {
  const el = _el('revenueComplianceFindingsCount');
  if (!el) return;
  if (state.loading) { el.textContent = '· loading…'; return; }
  if (state.error) { el.textContent = ''; return; }
  const n = (state.items || []).length;
  el.textContent = n ? `· ${n} flagged` : '· none flagged';
}

function renderTable() {
  renderFindingsCount();
  const tbody = _el('revenueComplianceTbody');
  if (!tbody) return;
  if (state.loading) { tbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">Loading…</td></tr>'; return; }
  if (state.error) { tbody.innerHTML = `<tr><td colspan="9" style="color:var(--err);">${escapeHtml(state.error)}</td></tr>`; return; }
  if (!state.items.length) { tbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">No flagged customers.</td></tr>'; return; }
  tbody.innerHTML = state.items.map((f) => {
    const flags = (f.flags || []).map((fl) => FLAG_LABELS[fl] || fl).join(', ');
    const overdueHot = (f.flags || []).includes('overdue_connected');
    return `<tr${overdueHot ? ' style="background:rgba(245,158,11,0.06);"' : ''}>
      <td>${escapeHtml(f.display_name || f.porta_customer_id || '—')}</td>
      <td>${escapeHtml(f.customer_class || '—')}</td>
      <td>${escapeHtml(f.account_state || '—')}</td>
      <td style="font-size:11px;">${escapeHtml(flags)}</td>
      <td>${f.overdue_balance != null ? _money(f.overdue_balance) : '—'}</td>
      <td>${f.days_overdue != null ? f.days_overdue : '—'}</td>
      <td>${f.monthly_fee != null ? _money(f.monthly_fee) : '—'}</td>
      <td>${f.paid_in_period ? 'yes' : 'no'}</td>
      <td>${f.credit_amount_period ? _money(f.credit_amount_period) : '—'}</td>
    </tr>`;
  }).join('');
}

function renderMeta() {
  const meta = _el('revenueComplianceMeta');
  if (!meta) return;
  if (!state.run) { meta.textContent = ''; return; }
  const when = state.run.computed_at ? formatDateTimeFull(state.run.computed_at) : '';
  meta.textContent = `${state.total} flagged · last run ${when}`;
  const status = _el('revenueComplianceStatus');
  if (status) {
    status.textContent = state.run.data_age_note || '';
    status.hidden = !status.textContent;
  }
}

function renderCreditBadge() {
  const badge = _el('revenueCreditReviewBadge');
  if (!badge) return;
  const n = creditState.pendingCount || 0;
  badge.textContent = n ? `· ${n} pending · ${_money(creditState.pendingAmount || 0)}` : '';
}

function renderCreditReviews() {
  const tbody = _el('revenueCreditReviewTbody');
  if (!tbody) return;
  if (creditState.loading) { tbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>'; return; }
  if (creditState.error) { tbody.innerHTML = `<tr><td colspan="8" style="color:var(--err);">${escapeHtml(creditState.error)}</td></tr>`; return; }
  if (!creditState.items.length) { tbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No manual credits for this filter.</td></tr>'; return; }
  tbody.innerHTML = creditState.items.map((c) => {
    const pending = c.review_status === 'pending';
    const statusLabel = c.reviewed_by
      ? `${escapeHtml(c.review_status)} · ${escapeHtml(c.reviewed_by)}`
      : escapeHtml(c.review_status || 'pending');
    const actions = pending
      ? `<button type="button" class="btn ghost" data-credit-review="${c.i_xdr}" data-status="reviewed">Mark reviewed</button>
         <button type="button" class="btn ghost" data-credit-review="${c.i_xdr}" data-status="flagged">Flag</button>`
      : `<button type="button" class="btn ghost" data-credit-review="${c.i_xdr}" data-status="pending">Reopen</button>`;
    return `<tr>
      <td style="white-space:nowrap;">${c.txn_time ? escapeHtml(formatDate(c.txn_time)) : '—'}</td>
      <td>${escapeHtml(c.display_name || '—')}</td>
      <td>${escapeHtml(c.porta_customer_id || '—')}</td>
      <td style="text-align:right;">${c.amount != null ? _money(c.amount) : '—'}</td>
      <td>${escapeHtml(c.operator_id || '—')}</td>
      <td style="font-size:11px;">${escapeHtml(c.description || '—')}</td>
      <td style="font-size:11px;">${statusLabel}</td>
      <td style="white-space:nowrap;">${actions}</td>
    </tr>`;
  }).join('');
}

// Manual credits and manual charges are told apart only by their description, so the
// dropdown is built from the descriptions actually present at the current status.
function renderCreditDescriptions() {
  const sel = _el('revenueCreditReviewDescription');
  if (!sel) return;
  const chosen = creditState.description || '';
  const opts = ['<option value="">All descriptions</option>'].concat(
    (creditState.descriptions || []).map((d) =>
      `<option value="${escapeHtml(d)}"${d === chosen ? ' selected' : ''}>${escapeHtml(d)}</option>`),
  );
  // Keep a hand-typed/stale choice selectable so the filter does not silently reset.
  if (chosen && !(creditState.descriptions || []).includes(chosen)) {
    opts.push(`<option value="${escapeHtml(chosen)}" selected>${escapeHtml(chosen)}</option>`);
  }
  sel.innerHTML = opts.join('');
}

async function loadCredits({ force = false } = {}) {
  if (creditState.loading) return;
  if (creditState.loaded && !force) { renderCreditReviews(); renderCreditBadge(); return; }
  creditState.loading = true; creditState.error = null;
  renderCreditReviews();
  try {
    let qs = `status=${encodeURIComponent(creditState.status)}`;
    if (creditState.description) qs += `&description=${encodeURIComponent(creditState.description)}`;
    const data = await adminAPI.fetch(API.revenueComplianceCredits(qs), { method: 'GET', cache: 'no-store' });
    creditState.items = data.items || [];
    creditState.descriptions = data.descriptions || [];
    renderCreditDescriptions();
    creditState.pendingCount = data.pending_count || 0;
    creditState.pendingAmount = data.pending_amount || 0;
    creditState.loaded = true;
  } catch (err) {
    creditState.error = adminAPI.formatErrorDetail(err);
  } finally {
    creditState.loading = false;
    renderCreditReviews();
    renderCreditBadge();
  }
}

async function reviewCredit(iXdr, newStatus) {
  try {
    let note = null;
    if (newStatus === 'flagged') {
      note = window.prompt('Reason for flagging this credit (optional):') || null;
    }
    await adminAPI.fetch(API.revenueComplianceCreditReview(iXdr), {
      method: 'POST', body: JSON.stringify({ status: newStatus, note }),
      headers: { 'Content-Type': 'application/json' },
    });
    showFlash('Credit review updated.', 'success');
    await loadCredits({ force: true });
  } catch (err) {
    showFlash(`Update failed: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

function renderAll() { renderKpis(); renderByClass(); renderTable(); renderMeta(); renderCreditBadge(); }

async function load({ force = false } = {}) {
  if (state.loading) return;
  if (state.loaded && !force) { renderAll(); return; }
  state.loading = true; state.error = null;
  renderTable();
  try {
    const qs = state.flag ? `flag=${encodeURIComponent(state.flag)}` : '';
    const data = await adminAPI.fetch(API.revenueCompliance(qs), { method: 'GET', cache: 'no-store' });
    state.run = data.run;
    state.summary = data.summary || {};
    state.items = data.items || [];
    state.total = data.total || 0;
    state.loaded = true;
  } catch (err) {
    state.error = adminAPI.formatErrorDetail(err);
  } finally {
    state.loading = false;
    renderAll();
  }
}

async function recompute() {
  const btn = _el('revenueComplianceRecomputeBtn');
  if (btn) btn.disabled = true;
  try {
    await adminAPI.fetch(API.revenueComplianceRecompute(), { method: 'POST' });
    showFlash('Revenue compliance recomputed.', 'success');
    await load({ force: true });
  } catch (err) {
    showFlash(`Recompute failed: ${adminAPI.formatErrorDetail(err)}`, 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

let _wired = false;
function wire() {
  if (_wired) return;
  if (!_el('toolsRevenueCompliancePanel')) return;
  _wired = true;
  const flag = _el('revenueComplianceFlag');
  if (flag) flag.addEventListener('change', () => { state.flag = flag.value; load({ force: true }); });
  const refresh = _el('revenueComplianceRefreshBtn');
  if (refresh) refresh.addEventListener('click', () => load({ force: true }));
  const recomputeBtn = _el('revenueComplianceRecomputeBtn');
  if (recomputeBtn) recomputeBtn.addEventListener('click', () => recompute());
  const exportBtn = _el('revenueComplianceExportBtn');
  if (exportBtn) exportBtn.addEventListener('click', () => {
    const a = document.createElement('a');
    a.href = API.revenueComplianceExport();
    a.download = 'revenue_compliance.csv';
    document.body.appendChild(a); a.click(); a.remove();
  });

  // Export the credits queue AS FILTERED — the endpoint takes the same status and
  // description params as the listing, so you get the rows you are looking at.
  const creditExport = _el('revenueCreditReviewExportBtn');
  if (creditExport) creditExport.addEventListener('click', () => {
    const p = new URLSearchParams({ status: creditState.status || 'pending' });
    if (creditState.description) p.set('description', creditState.description);
    const a = document.createElement('a');
    a.href = API.revenueComplianceCreditsExport(p.toString());
    a.download = '';   // let the server's Content-Disposition name it per filter
    document.body.appendChild(a); a.click(); a.remove();
  });

  // Credits pending review — lazy-load when the section is first expanded.
  const creditSection = _el('revenueCreditReviewSection');
  if (creditSection) creditSection.addEventListener('toggle', () => {
    if (creditSection.open) loadCredits();
  });
  const creditDesc = _el('revenueCreditReviewDescription');
  if (creditDesc) creditDesc.addEventListener('change', () => {
    creditState.description = creditDesc.value; loadCredits({ force: true });
  });
  const creditStatus = _el('revenueCreditReviewStatus');
  if (creditStatus) creditStatus.addEventListener('change', () => {
    creditState.status = creditStatus.value; loadCredits({ force: true });
  });
  const creditRefresh = _el('revenueCreditReviewRefreshBtn');
  if (creditRefresh) creditRefresh.addEventListener('click', () => loadCredits({ force: true }));
  const creditTbody = _el('revenueCreditReviewTbody');
  if (creditTbody) creditTbody.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-credit-review]');
    if (!btn) return;
    reviewCredit(btn.getAttribute('data-credit-review'), btn.getAttribute('data-status'));
  });
}

function mount() { wire(); load(); loadCredits(); }

export default { mount, load, render: renderAll, state };
